<template>
  <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
      <div class="flex items-start justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold dark:text-gray-100">Edit history</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Recent changes to this user. Records older than one year are purged automatically.
          </p>
        </div>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white text-2xl leading-none">&times;</button>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-if="error" class="text-red-500 text-sm py-4">{{ error }}</div>
        <div v-else-if="!loading && !entries.length" class="text-center py-8 text-gray-500 dark:text-gray-400">No edits recorded.</div>
        <table v-if="entries.length" class="w-full text-sm">
          <thead class="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="text-left py-2 px-2">When</th>
              <th class="text-left py-2 px-2">Action</th>
              <th class="text-left py-2 px-2">Field</th>
              <th class="text-left py-2 px-2">Old</th>
              <th class="text-left py-2 px-2">New</th>
              <th class="text-left py-2 px-2">By</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in entries" :key="row.id" class="border-b border-gray-100 dark:border-gray-700 align-top">
              <td class="py-2 px-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">{{ formatDate(row.created_at) }}</td>
              <td class="py-2 px-2">
                <span :class="actionClass(row.action)" class="inline-block text-xs px-2 py-0.5 rounded">{{ row.action }}</span>
              </td>
              <td class="py-2 px-2 text-gray-700 dark:text-gray-200">{{ row.field_name || '—' }}</td>
              <td class="py-2 px-2 text-gray-600 dark:text-gray-300 break-all">{{ truncate(row.old_value) }}</td>
              <td class="py-2 px-2 text-gray-900 dark:text-gray-100 break-all">{{ truncate(row.new_value) }}</td>
              <td class="py-2 px-2 text-gray-700 dark:text-gray-200">{{ row.changed_by_name || 'System' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-end mt-4">
        <button @click="$emit('close')"
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100 rounded">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    isVisible: { type: Boolean, default: false },
    userId: { type: [Number, String], default: null },
  },
  emits: ['close'],
  data() {
    return {
      entries: [],
      loading: false,
      error: null,
    };
  },
  watch: {
    isVisible(val) {
      if (val && this.userId) this.load();
    },
    userId(val) {
      if (this.isVisible && val) this.load();
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.$updateLoading(1);
      try {
        const res = await axios.get(`users/${this.userId}/edit-log`, { params: { limit: 200 } });
        this.entries = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.error = e?.response?.data?.message || 'Failed to load edit history.';
        this.entries = [];
      } finally {
        this.loading = false;
        this.$updateLoading(-1);
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleString();
    },
    truncate(v) {
      if (v == null || v === '') return '—';
      const s = String(v);
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    },
    actionClass(action) {
      switch (action) {
        case 'create': return 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100';
        case 'update': return 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100';
        case 'delete': return 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-100';
        default:       return 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-100';
      }
    },
  },
};
</script>
